import React, { useMemo, useEffect, useState } from "react";
import styles from "./select-stations-modal.module.scss";
import { Field, Filter, SearchField, Autocomplete, Button } from "components/commons";
import locale from "localization";
import { Close } from "@material-ui/icons";
import StationType from "enums/station-type";
import StationStatusEnum from "enums/station-status";
import { prettifyStationStatus } from "utils/pretty.utils";
import { Island, Region } from "assets";

const SelectStationsFilter = ({
  filterState,
  modifyFilter,
  filterCount,
  applyFilter,
  applyClearFilter,
  searchKey,
  modifySearch,
  applySearch,
  applyClearSearch,
  resetFilter,
  countSelected = 0,
  actionText,
  actionProps,
  actionOnClick,
  uniqueStationType = [...Object.values(StationType).map((value) => ({ label: value, value }))],
  uniqueStatus = [
    ...Object.values(StationStatusEnum).map((value) => ({
      label: prettifyStationStatus(value),
      value,
    })),
  ],
  uniqueIsland = [
    ...Object.values(Island).map((island) => ({
      label: island.label,
      value: island.value,
    })),
  ],
  disabled = { stationType: false, status: false },
}) => {
  const [selectedIslands, setSelectedIslands] = useState([]);

  // Track selected islands from filterState
  useEffect(() => {
    if (filterState?.island?.value) {
      setSelectedIslands(filterState.island.value);
    } else {
      setSelectedIslands([]);
    }
  }, [filterState?.island?.value]);

  // Filter regions based on selected islands
  const uniqueRegion = useMemo(() => {
    // If no islands selected or "All" is selected, show all regions
    if (!selectedIslands || selectedIslands.length === 0 || filterState?.island?.isSelectedAll) {
      return Region.map((region) => ({
        label: region.label,
        value: region.value,
      }));
    }

    // Filter regions based on selected islands
    return Region.filter((region) =>
      selectedIslands.some((island) => region.island === island)
    ).map((region) => ({
      label: region.label,
      value: region.value,
    }));
  }, [selectedIslands, filterState?.island?.isSelectedAll]);

  // Handle island change to reset region if needed
  const handleIslandChange = (name, { value, isSelectedAll }) => {
    modifyFilter({
      [name]: { value, isSelectedAll },
      // Reset region when island changes
      region: { value: [], isSelectedAll: false },
    });
  };
  return (
    <div className={styles?.container}>
      <div className={styles?.filter}>
        <Filter
          close={resetFilter}
          clear={applyClearFilter}
          submit={() => {
            applyFilter();
          }}
          filterCount={filterCount}
        >
          <Field
            label={locale.stationType}
            labelPosition={"left"}
            className={styles.fieldSelect}
            childrenClassName={styles.fieldContent}
          >
            <Autocomplete
              name={"stationType"}
              hasAll
              multiple
              {...filterState?.stationType}
              options={uniqueStationType}
              onChange={(name, { value, isSelectedAll }) =>
                modifyFilter({ [name]: { value, isSelectedAll } })
              }
              disabled={disabled.stationType}
            />
          </Field>
          <Field
            label={locale.status}
            labelPosition={"left"}
            className={styles.fieldSelect}
            childrenClassName={styles.fieldContent}
          >
            <Autocomplete
              name={"status"}
              hasAll
              multiple
              {...filterState?.status}
              options={uniqueStatus}
              onChange={(name, { value, isSelectedAll }) =>
                modifyFilter({ [name]: { value, isSelectedAll } })
              }
              disabled={disabled.status}
            />
          </Field>
          <Field
            label={locale.island}
            labelPosition={"left"}
            className={styles.fieldSelect}
            childrenClassName={styles.fieldContent}
          >
            <Autocomplete
              name={"island"}
              hasAll
              multiple
              {...filterState?.island}
              options={uniqueIsland}
              onChange={handleIslandChange}
              disabled={disabled.island}
            />
          </Field>
          <Field
            label={locale.region}
            labelPosition={"left"}
            className={styles.fieldSelect}
            childrenClassName={styles.fieldContent}
          >
            <Autocomplete
              name={"region"}
              hasAll
              multiple
              {...filterState?.region}
              options={uniqueRegion}
              onChange={(name, { value, isSelectedAll }) =>
                modifyFilter({ [name]: { value, isSelectedAll } })
              }
              disabled={disabled.region}
            />
          </Field>
        </Filter>
        <Field className={styles.search}>
          <SearchField
            restrict={false}
            searchWithSymbols
            placeholder={[locale.stationCode, locale.stationName].join(", ")}
            value={searchKey}
            onChange={(_, { value }) => {
              modifySearch(value);
            }}
            onEnter={applySearch}
            onClear={applyClearSearch}
          />
        </Field>
        {actionText && (
          <Field className={styles.clearSelectedButton}>
            <Button onClick={actionOnClick} link {...(actionProps || {})}>
              {actionText}
              <Close fontSize="small" />
            </Button>
          </Field>
        )}
      </div>
    </div>
  );
};

export default SelectStationsFilter;
